import type { SVGProps } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import type { Avatar as AvatarT } from '@/api/types';

/** Original decorative illustrations built from the same shape language as the avatars. */

export function Squiggle({ color = '#1A1A1A', ...p }: { color?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 120 24" fill="none" aria-hidden {...p}>
      <path d="M2 14c10-12 18-12 26 0s16 12 24 0 16-12 24 0 16 12 24 0 12-10 18-4" stroke={color} strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

export function Burst({ color = '#1A1A1A', ...p }: { color?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 40 40" aria-hidden {...p}>
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i * Math.PI) / 4;
        return <line key={i} x1={20 + 9 * Math.cos(a)} y1={20 + 9 * Math.sin(a)} x2={20 + 18 * Math.cos(a)} y2={20 + 18 * Math.sin(a)} stroke={color} strokeWidth="3.5" strokeLinecap="round" />;
      })}
    </svg>
  );
}

export function Spark({ color = '#F2C21A', ...p }: { color?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...p}>
      <path d="M12 0c.8 6.4 4.6 10.6 12 12-7.4 1.4-11.2 5.6-12 12-.8-6.4-4.6-10.6-12-12C7.4 10.6 11.2 6.4 12 0Z" fill={color} />
    </svg>
  );
}

const A = (hue: number, shape: AvatarT['shape'], face: AvatarT['face']): AvatarT => ({ hue, shape, face });

/** Two characters with a dotted line waiting to connect — Your People empty state. */
export function EmptyPeopleArt({ className }: { className?: string }) {
  return (
    <div className={className} aria-hidden>
      <div className="relative mx-auto h-36 w-64">
        <svg viewBox="0 0 256 144" className="absolute inset-0 h-full w-full">
          <path d="M70 84c30-40 86-40 116 0" fill="none" stroke="currentColor" strokeWidth="4" strokeDasharray="2 12" strokeLinecap="round" />
          <circle cx="128" cy="52" r="16" fill="#F2C21A" />
          <path d="M128 45v14M121 52h14" stroke="#1A1A1A" strokeWidth="3.5" strokeLinecap="round" />
        </svg>
        <div className="absolute bottom-0 left-4 -rotate-6">
          <Avatar avatar={A(195, 'blob', 'smile')} size={84} />
        </div>
        <div className="absolute bottom-0 right-4 rotate-6">
          <Avatar avatar={A(32, 'flower', 'wink')} size={84} />
        </div>
      </div>
    </div>
  );
}

/** A ticket-like stub with a star — Drops empty / disabled state. */
export function DropArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 220 140" className={className} aria-hidden>
      <g transform="rotate(-6 110 70)">
        <path d="M30 30h160a8 8 0 0 1 8 8v18a14 14 0 0 0 0 28v18a8 8 0 0 1-8 8H30a8 8 0 0 1-8-8V84a14 14 0 0 0 0-28V38a8 8 0 0 1 8-8Z" fill="#ED4B44" />
        <path d="M150 34v72" stroke="#1A1A1A" strokeWidth="3" strokeDasharray="4 7" />
        <path d="M86 46l6 14 15 2-11 10 3 15-13-8-13 8 3-15-11-10 15-2Z" fill="#1A1A1A" />
        <rect x="160" y="52" width="24" height="8" rx="4" fill="#1A1A1A" />
        <rect x="160" y="68" width="16" height="8" rx="4" fill="#1A1A1A" />
      </g>
    </svg>
  );
}

/** An unplugged cable character — error states. */
export function UnpluggedArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 110" className={className} aria-hidden>
      <path d="M4 70h52a10 10 0 0 0 10-10v-6" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <rect x="54" y="30" width="24" height="26" rx="6" fill="currentColor" />
      <path d="M60 30V18M72 30V18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M196 70h-52a10 10 0 0 1-10-10v-6" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <rect x="118" y="30" width="28" height="26" rx="6" fill="#ED4B44" />
      <circle cx="127" cy="41" r="2.6" fill="#1A1A1A" />
      <circle cx="137" cy="41" r="2.6" fill="#1A1A1A" />
      <path d="M126 50q6-4 12 0" stroke="#1A1A1A" strokeWidth="2.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/** A little cast of characters peeking out — used in landing + match search. */
export const CAST: AvatarT[] = [
  A(12, 'blob', 'grin'),
  A(84, 'star', 'cool'),
  A(258, 'flower', 'smile'),
  A(192, 'circle', 'wink'),
  A(45, 'bolt', 'chill'),
  A(330, 'blob', 'smile'),
];
